import React, { useEffect, useState } from "react";
import { adminGet, adminPost } from "../common/api/api";
import { setCurrency } from "../App";

const DEFAULT_SETTINGS = {
  site_name: "",
  site_tagline: "",
  contact_email: "",
  contact_phone: "",
  currency: "Rs.",
  free_shipping_threshold: "0",
  maintenance_mode: "false",
  facebook_url: "",
  instagram_url: "",
  twitter_url: "",
  footer_text: "",
};

const CURRENCY_OPTIONS = [
  { value: "Rs.", label: "PKR (Rs.)" },
  { value: "$", label: "USD ($)" },
  { value: "€", label: "EUR (€)" },
  { value: "£", label: "GBP (£)" },
  { value: "AED", label: "AED" },
];

const SETTING_GROUPS = [
  {
    title: "General",
    icon: "🏪",
    keys: [
      { key: "site_name", label: "Site Name", placeholder: "e.g. TechStore" },
      { key: "site_tagline", label: "Tagline", placeholder: "e.g. Best Tech Deals" },
      { key: "footer_text", label: "Footer Text", placeholder: "© 2024 TechStore" },
    ],
  },
  {
    title: "Contact",
    icon: "📞",
    keys: [
      { key: "contact_email", label: "Contact Email", placeholder: "contact@yourstore.com" },
      { key: "contact_phone", label: "Contact Phone", placeholder: "+1 234 567 890" },
    ],
  },
  {
    title: "Commerce",
    icon: "💰",
    keys: [
      { key: "currency", label: "Currency", placeholder: "Rs." },
      { key: "free_shipping_threshold", label: "Free Shipping Above", placeholder: "100" },
    ],
  },
  {
    title: "Social Media",
    icon: "📱",
    keys: [
      { key: "facebook_url", label: "Facebook URL", placeholder: "https://facebook.com/..." },
      { key: "instagram_url", label: "Instagram URL", placeholder: "https://instagram.com/..." },
      { key: "twitter_url", label: "Twitter / X URL", placeholder: "https://x.com/..." },
    ],
  },
];

const AdminSettings = () => {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [maintenance, setMaintenance] = useState(false);

  const fetchSettings = async () => {
    setLoading(true);
    const data = await adminGet("/getsettings");
    const merged = { ...DEFAULT_SETTINGS, ...data };
    setSettings(merged);
    setMaintenance(merged.maintenance_mode === "true");
    setCurrency(merged.currency || "Rs.");
    setLoading(false);
  };

  useEffect(() => { fetchSettings(); }, []);

  const saveAll = async () => {
    setSaving(true);
    const all = { ...settings, maintenance_mode: String(maintenance) };
    await Promise.all(
      Object.entries(all).map(([key, value]) =>
        adminPost("/updatesetting", { key, value: value || "" })
      )
    );
    setCurrency(all.currency || "Rs.");
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleChange = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const inputClass = "w-full bg-white/[0.05] border border-white/[0.1] rounded-lg px-4 py-2.5 text-[#e2e8f0] placeholder-[#4a5568] focus:outline-none focus:border-[#00d4ff]/60 focus:bg-white/[0.08] transition-all text-sm";
  const labelClass = "block text-sm font-medium text-[#a0aec0] mb-1.5";

  return (
    <div className="p-6 max-w-2xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Settings</h1>
          <p className="text-sm text-[#4a5568] mt-0.5">Configure your store settings</p>
        </div>
        <button
          onClick={saveAll}
          disabled={saving}
          className="px-5 py-2.5 bg-gradient-to-r from-[#00d4ff] to-[#0099cc] text-[#060c18] font-bold rounded-lg text-sm hover:opacity-90 transition-opacity cursor-pointer disabled:opacity-50"
        >
          {saving ? "Saving..." : "Save All"}
        </button>
      </div>

      {saved && (
        <div className="mb-5 px-4 py-3 rounded-xl bg-green-500/10 border border-green-500/30 text-green-400 text-sm font-medium">
          ✓ Settings saved successfully!
        </div>
      )}

      {loading ? (
        <div className="text-[#4a5568] py-16 text-center">Loading settings...</div>
      ) : (
        <div className="space-y-6">
          {SETTING_GROUPS.map((group) => (
            <div key={group.title} className="bg-white/[0.02] border border-white/[0.07] rounded-xl p-5">
              <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
                <span>{group.icon}</span> {group.title}
              </h3>
              <div className="space-y-4">
                {group.keys.map(({ key, label, placeholder }) => (
                  <div key={key}>
                    <label className={labelClass}>{label}</label>
                    {key === "currency" ? (
                      <>
                        <select
                          value={settings[key] || "Rs."}
                          onChange={(e) => handleChange(key, e.target.value)}
                          className={inputClass}
                        >
                          {CURRENCY_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                        <p className="text-[11px] text-[#4a5568] mt-1">
                          This symbol is used across the store for product prices, cart totals, and payment summaries.
                        </p>
                      </>
                    ) : (
                      <input
                        type="text"
                        value={settings[key] || ""}
                        onChange={(e) => handleChange(key, e.target.value)}
                        placeholder={placeholder}
                        className={inputClass}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Maintenance Mode */}
          <div className="bg-white/[0.02] border border-white/[0.07] rounded-xl p-5">
            <h3 className="text-sm font-bold text-white mb-4">🔧 Maintenance</h3>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#e2e8f0] font-medium">Maintenance Mode</p>
                <p className="text-xs text-[#4a5568] mt-0.5">When enabled, the store will show a maintenance page to visitors</p>
              </div>
              <button
                type="button"
                onClick={() => setMaintenance(!maintenance)}
                className={`relative w-12 h-6 rounded-full transition-colors duration-200 cursor-pointer focus:outline-none shrink-0 ${maintenance ? "bg-[#00d4ff]" : "bg-white/[0.1]"}`}
              >
                <span
                  className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200"
                  style={{ transform: maintenance ? "translateX(24px)" : "translateX(0)" }}
                />
              </button>
            </div>
            {maintenance && (
              <div className="mt-3 px-3 py-2 rounded-lg bg-yellow-400/10 border border-yellow-400/20 text-yellow-400 text-xs">
                ⚠ Maintenance mode is ON. Remember to save.
              </div>
            )}
          </div>

          {/* Save Button at bottom */}
          <button
            onClick={saveAll}
            disabled={saving}
            className="w-full py-3 bg-gradient-to-r from-[#00d4ff] to-[#0099cc] text-[#060c18] font-bold rounded-xl text-sm hover:opacity-90 transition-opacity cursor-pointer disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save All Settings"}
          </button>
          <p className="text-xs text-[#4a5568] text-center mt-2">
            Tip: PKR uses <span className="text-[#00d4ff] font-semibold">Rs.</span> as the store symbol.
          </p>
        </div>
      )}
    </div>
  );
};

export default AdminSettings;
